import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  QaiAlertComponent,
  QaiAvatarComponent,
  QaiButtonComponent,
  QaiCallAudioIndicatorComponent,
  QaiCallCaptionsComponent,
  QaiCallTimerComponent,
  QaiCallVideoComponent,
  QaiDialogComponent,
  QaiIconComponent,
  QaiImageComponent,
  QaiLabelComponent,
  QaiLoadingComponent,
  QaiPageNavigatorComponent,
  QaiVideoNameBadgeComponent,
  QaiSpinnerComponent,
  QaiTooltipComponent,
  QaiFloatingDirective
} from '@qai/ui';

import { PortalModule } from '@angular/cdk/portal';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule } from '@angular/forms';

import { SharedComponentsModule } from '../../../components/shared-components.module';
import { ButtonComponent } from '../../../components/shared/button/button.component';
import { TooltipComponent } from '../../../components/shared/tooltip/tooltip.component';
import { ModalComponent } from '../../../components/shared/modal/modal.component';

import { CallControlsComponent } from './call-practice/call-controls/call-controls';
import { CallFooterComponent } from './call-practice/call-footer/call-footer';
import { CallHeaderComponent } from './call-practice/call-header/call-header';
import { CallHintComponent } from './call-practice/call-hint/call-hint';
import { CallMainComponent } from './call-practice/call-main/call-main';

import { CallPracticeComponent } from './call-practice/call-practice.component';
import { CallComponent } from './call.component';
import { PreCallComponent } from './pre-call/pre-call.component';
import { PostCallComponent } from './post-call/post-call.component';
import { PostCallHiddenResultsComponent } from './post-call-hidden-results/post-call-hidden-results.component';
import { DeviceSettingsCallComponent } from './device-settings/device-settings-call/device-settings-call.component';
import { DeviceSettingsContentComponent } from './device-settings/device-settings-content/device-settings-content.component';
import { DeviceSettingsModalComponent } from './device-settings/device-settings-modal/device-settings-modal.component';
import { DeviceSettingsSpeechRecognitionComponent } from './device-settings/device-settings-speech-recognition/device-settings-speech-recognition.component';
import { CallTourComponent } from './call-tour/call-tour.component';
import { EndCallEarlyComponent } from './end-call-early/end-call-early.component';
import { CallPauseComponent } from './call-pause/call-pause.component';
import { NoResponseDetectedComponent } from './no-response-detected/no-response-detected.component';
import { CallVisualAidComponent } from './call-visual-aid/call-visual-aid.component';
import { CallVisualAidComponentV2 } from './call-visual-aid-v2/call-visual-aid-v2.component';
import { ErrorDisplayBannerComponent } from './error-display-banner/error-display-banner.component';
import { CallMainVisualAidComponent } from './call-practice/call-main-with-visual-aid/call-main-visual-aid';
import { CallTopAlertComponent } from './call-practice/call-top-alert/call-top-alert.component';
import { CallHintTourComponent } from './call-practice/call-hint-tour/call-hint-tour';
import { TranslocoRootModule } from 'src/app/transloco-root.module';
import { CallPracticeVisualAidComponent } from './call-practice-visual-aid/call-practice-visual-aid.component';
import { VisualAidCallFooterComponent } from './call-practice-visual-aid/visual-aid-call-footer/visual-aid-call-footer.component';
import { VisualAidCallControlsComponent } from './call-practice-visual-aid/visual-aid-call-controls/visual-aid-call-controls.component';
import { VisualAidCallMainComponent } from './call-practice-visual-aid/visual-aid-call-main/visual-aid-call-main.component';
import { VisualGuideComponent } from './call-practice-visual-aid/visual-aid-call-navigation/visual-guide.component';
import { VisualAidDeviceSettingsContentComponent } from './device-settings/visual-aid-device-settings/visual-aid-device-settings-content/visual-aid-device-settings-content.component';
import { DeviceSettingsHeaderComponent } from './device-settings/visual-aid-device-settings/device-settings-header/device-settings-header.component';
import { VisualAidNextPageNavigatorComponent } from './call-practice-visual-aid/visual-aid-next-page-navigator/visual-aid-next-page-navigator.component';
import { DeviceSettingsAudioCirclesComponent } from './device-settings/visual-aid-device-settings/device-settings-audio-circles/device-settings-audio-circles.component';
import { DeviceSettingsMicrophoneSelectionComponent } from './device-settings/visual-aid-device-settings/device-settings-microphone-selection/device-settings-microphone-selection.component';
import { GenderSelectionModalComponent } from './gender-selection-modal/gender-selection-modal.component';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { DeviceSettingsDiagnosticComponent } from './device-settings/device-settings-diagnostic/device-settings-diagnostic.component';
import { CallPathwayComponent } from './call-pathway/call-pathway.component';
import { CallLeaveEarlyComponent } from './call-pathway/call-leave-early/call-leave-early.component';
import { PathwayCallControlsComponent } from './call-pathway/call-controls/call-controls';
import { PathwayCallHeaderComponent } from './call-pathway/call-header/call-header';
import { PathwayCallFooterComponent } from './call-pathway/call-footer/call-footer';
import { PathwayCallMainComponent } from './call-pathway/call-main/call-main';
import { CallPathwayGuideComponent } from './call-pathway/call-flow/call-pathway-guide/call-pathway-guide.component';
import { CallPathwayPdfComponent } from './call-pathway/call-flow/call-pathway-pdf/call-pathway-pdf.component';
import { CallPathwayVideoComponent } from './call-pathway/call-flow/call-pathway-video/call-pathway-video.component';
import { QuizResult } from './call-pathway/quiz-result/quiz-result';
import { ResultsGuidedComponent } from 'src/app/pages/learner/results/results-guided/results-guided.component';
import { PathwayGuidedResultComponent } from 'src/app/pages/learner/results/results-guided/pathway-guided-result/pathway-guided-result.component';
import { HangTightComponent } from 'src/app/pages/learner/results/hang-tight/hang-tight.component';
import { ResultsSkillsComponent } from 'src/app/pages/learner/results/results-skills/results-skills.component';
import { ResultsTranscriptComponent } from 'src/app/pages/learner/results/results-transcript/results-transcript.component';
import { qaiUiImports } from 'src/app/qai-ui-imports';
import { CertificationAcknowledgementModalComponent } from 'src/app/components/modals/certification-acknowledgement-modal/certification-acknowledgement-modal.component';
import { CameraInactiveWarningComponent } from 'src/app/components/modals/camera-inactive-warning/camera-inactive-warning.component';
import { CallPathwayErrorComponent } from './call-pathway/call-flow/call-pathway-error/call-pathway-error.component';
import { CallPathwayRoleplayComponent } from './call-pathway-roleplay/call-pathway-roleplay.component';
import { CallPathwayRoleplayContentComponent } from './call-pathway-roleplay/call-roleplay-content/call-roleplay-content.component';
import { CallPreambleComponent } from './call-pathway-roleplay/call-roleplay-preamble/call-roleplay-preamble.component';
import { RoleplayCallFooterComponent } from './call-pathway-roleplay/call-roleplay-footer/roleplay-footer.component';
import { CallRoleplayResultsComponent } from './call-pathway-roleplay/call-roleplay-results/call-roleplay-results.component';
import { ResultsPathwayRoleplayComponent } from 'src/app/pages/learner/results/results-pathway-roleplay/results-pathway-roleplay.component';
import { ResultOverallScoreComponent } from 'src/app/pages/learner/results/results-pathway-roleplay/result-overall-score/result-overall-score.component';

@NgModule({
  declarations: [
    CallComponent,
    CallPauseComponent,
    CallPracticeComponent,
    CallTourComponent,
    CallVisualAidComponent,
    DeviceSettingsCallComponent,
    DeviceSettingsContentComponent,
    DeviceSettingsModalComponent,
    DeviceSettingsSpeechRecognitionComponent,
    EndCallEarlyComponent,
    NoResponseDetectedComponent,
    PostCallComponent,
    PreCallComponent,
    CallVisualAidComponentV2,
    CallPracticeVisualAidComponent,
    VisualAidDeviceSettingsContentComponent,
    DeviceSettingsMicrophoneSelectionComponent,
    DeviceSettingsDiagnosticComponent,
    CallPathwayComponent,
    CallLeaveEarlyComponent,
    QuizResult,
    ResultsGuidedComponent,
    PathwayGuidedResultComponent,
    HangTightComponent,
    ResultsSkillsComponent,
    ResultsTranscriptComponent,
    CallPathwayRoleplayComponent,
  ],
  imports: [
    ButtonComponent,
    CallControlsComponent,
    CallFooterComponent,
    CallHeaderComponent,
    CallHintComponent,
    CallHintTourComponent,
    CallMainComponent,
    CommonModule,
    FormsModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatTooltipModule,
    ModalComponent,
    PortalModule,
    QaiAlertComponent,
    QaiAvatarComponent,
    QaiButtonComponent,
    QaiCallAudioIndicatorComponent,
    QaiCallCaptionsComponent,
    QaiCallTimerComponent,
    QaiCallVideoComponent,
    QaiDialogComponent,
    QaiIconComponent,
    QaiVideoNameBadgeComponent,
    QaiSpinnerComponent,
    QaiTooltipComponent,
    QaiFloatingDirective,
    SharedComponentsModule,
    TooltipComponent,
    CallMainVisualAidComponent,
    QaiPageNavigatorComponent,
    QaiImageComponent,
    ErrorDisplayBannerComponent,
    CallTopAlertComponent,
    TranslocoRootModule,
    VisualAidCallControlsComponent,
    VisualAidCallFooterComponent,
    VisualAidCallMainComponent,
    VisualGuideComponent,
    DeviceSettingsHeaderComponent,
    VisualAidNextPageNavigatorComponent,
    DeviceSettingsAudioCirclesComponent,
    QaiLabelComponent,
    QaiLoadingComponent,
    GenderSelectionModalComponent,
    PostCallHiddenResultsComponent,
    PathwayCallControlsComponent,
    PathwayCallHeaderComponent,
    PathwayCallFooterComponent,
    PathwayCallMainComponent,
    CallPathwayGuideComponent,
    CallPathwayErrorComponent,
    CallPathwayPdfComponent,
    CallPathwayVideoComponent,
    MatTabsModule,
    MatMenuModule,
    qaiUiImports,
    CertificationAcknowledgementModalComponent,
    CameraInactiveWarningComponent,
    InlineSVGModule,
    CallPathwayRoleplayContentComponent,
    CallPreambleComponent,
    RoleplayCallFooterComponent,
    CallRoleplayResultsComponent,
    ResultsPathwayRoleplayComponent,
    ResultOverallScoreComponent,
  ],
  providers: [
    LoggerService
  ],
  exports: [
    CallComponent,
    ResultsGuidedComponent,
    HangTightComponent,
    ResultsSkillsComponent,
    ResultsTranscriptComponent,
  ],
})
export class CallModule {}
